import React from 'react';
import { useAuthContext } from '../../../context/AuthContext';

export type ForgeEnvironmentId = 'prod' | 'staging' | 'local';

type LocationLike = Pick<Location, 'origin' | 'pathname' | 'search' | 'hash'>;

interface ForgeEnvironmentLink {
  id: ForgeEnvironmentId;
  label: string;
  title: string;
  href: string;
  isActive: boolean;
}

interface ForgeAppLink {
  id: 'forge' | 'control' | 'eval';
  label: string;
  shortLabel: string;
  title: string;
  href: string;
  isActive: boolean;
}

const FORGE_ENVIRONMENTS: Array<{
  id: ForgeEnvironmentId;
  label: string;
  origin: string;
  title: string;
}> = [
  {
    id: 'prod',
    label: 'Prod',
    origin: 'https://control.focal.systems',
    title: 'Open this page in production',
  },
  {
    id: 'staging',
    label: 'Stg',
    origin: 'https://control.staging.focal.dev',
    title: 'Open this page in staging',
  },
  {
    id: 'local',
    label: 'Local',
    origin: 'http://localhost:3002',
    title: 'Open this page locally',
  },
];

const LOCAL_ORIGIN_PATTERN = /^http:\/\/(?:localhost|127\.0\.0\.1):\d+$/;

const FORGE_APP_LINKS: Array<Omit<ForgeAppLink, 'isActive'>> = [
  { id: 'forge', label: 'Forge', shortLabel: 'F', title: 'Open Forge', href: '/forge' },
  {
    id: 'control',
    label: 'Control',
    shortLabel: 'C',
    title: 'Open Control store selection',
    href: '/stores',
  },
  {
    id: 'eval',
    label: 'Eval',
    shortLabel: 'E',
    title: 'Open Eval',
    href: '/eval',
  },
];

const isLocalForgeOrigin = (origin: string): boolean => LOCAL_ORIGIN_PATTERN.test(origin);

export const getCurrentForgeEnvironment = (origin: string): ForgeEnvironmentId | undefined => {
  if (origin === 'https://control.focal.systems') return 'prod';
  if (origin === 'https://control.staging.focal.dev') return 'staging';
  if (isLocalForgeOrigin(origin)) return 'local';
  return undefined;
};

const getForgeEnvironmentOrigin = (
  environment: (typeof FORGE_ENVIRONMENTS)[number],
  currentOrigin: string
): string => {
  if (environment.id === 'local' && isLocalForgeOrigin(currentOrigin)) {
    return currentOrigin;
  }

  return environment.origin;
};

export const getCurrentForgeRoute = (location: LocationLike): string => {
  if (!location.pathname.startsWith('/forge') && !location.pathname.startsWith('/eval')) {
    return '/forge';
  }

  return `${location.pathname}${location.search}${location.hash}`;
};

export const buildForgeEnvironmentLinks = (
  location: LocationLike = window.location
): ForgeEnvironmentLink[] => {
  const route = getCurrentForgeRoute(location);
  const activeEnvironment = getCurrentForgeEnvironment(location.origin);

  return FORGE_ENVIRONMENTS.map((environment) => ({
    id: environment.id,
    label: environment.label,
    title: environment.title,
    href: `${getForgeEnvironmentOrigin(environment, location.origin)}${route}`,
    isActive: environment.id === activeEnvironment,
  }));
};

export const buildForgeAppLinks = (location: LocationLike = window.location): ForgeAppLink[] =>
  FORGE_APP_LINKS.map((link) => ({
    ...link,
    isActive:
      (link.id === 'forge' && location.pathname.startsWith('/forge')) ||
      (link.id === 'eval' && location.pathname.startsWith('/eval')) ||
      (link.id === 'control' &&
        !location.pathname.startsWith('/forge') &&
        !location.pathname.startsWith('/eval')),
  }));

export const ForgeEnvironmentSwitcher: React.FC = () => {
  const { hasInternalAccess } = useAuthContext();

  if (!hasInternalAccess) {
    return null;
  }

  const links = buildForgeEnvironmentLinks();

  return (
    <div className="forge-env-switcher" role="group" aria-label="Open environment">
      {links.map((link) => (
        <a
          key={link.id}
          className={`forge-env-link ${link.isActive ? 'active' : ''}`}
          href={link.href}
          title={link.title}
          aria-current={link.isActive ? 'page' : undefined}
        >
          <span className="forge-env-label-full">{link.label}</span>
          <span className="forge-env-label-short" aria-hidden="true">
            {link.label[0]}
          </span>
        </a>
      ))}
    </div>
  );
};

export const ForgeAppSwitcher: React.FC = () => {
  const { hasInternalAccess } = useAuthContext();

  if (!hasInternalAccess) {
    return null;
  }

  const links = buildForgeAppLinks();

  return (
    <div className="forge-app-switcher" role="group" aria-label="Open Focal tool">
      {links.map((link) => (
        <a
          key={link.id}
          className={`forge-app-link ${link.isActive ? 'active' : ''}`}
          href={link.href}
          title={link.title}
          aria-current={link.isActive ? 'page' : undefined}
        >
          <span className="forge-app-label-full">{link.label}</span>
          <span className="forge-app-label-short" aria-hidden="true">
            {link.shortLabel}
          </span>
        </a>
      ))}
    </div>
  );
};
